import React, { useState, useEffect } from "react";
import { FormControl, InputLabel, Select, MenuItem, InputAdornment } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  Autocomplete,
  Avatar,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

// Example country code list
const COUNTRY_CODES = [
  { code: "+1", name: "United States" },
  { code: "+91", name: "India" },
  { code: "+44", name: "United Kingdom" },
  { code: "+61", name: "Australia" },
  { code: "+81", name: "Japan" },
  { code: "+49", name: "Germany" },
  { code: "+971", name: "UAE" },
  // ...add more as needed
];

const COUNTRY_CODES_LONGEST_FIRST = [...COUNTRY_CODES].sort(
  (a, b) => b.code.replace("+", "").length - a.code.replace("+", "").length
);

/** Split stored WhatsApp digits (+91 / 91 prefix) into selector + national number. */
function splitPhoneCountry(phone) {
  const raw = String(phone || "").trim();
  const digits = raw.replace(/\D/g, "");
  const fallback = COUNTRY_CODES[1];
  if (!digits) {
    return { countryCode: fallback.code, national: "" };
  }
  for (const c of COUNTRY_CODES_LONGEST_FIRST) {
    const cc = c.code.replace("+", "");
    if (raw.startsWith(c.code)) {
      const rest = raw.slice(c.code.length).replace(/\D/g, "");
      if (rest) return { countryCode: c.code, national: rest };
    }
    if (digits.startsWith(cc) && digits.length > cc.length) {
      return { countryCode: c.code, national: digits.slice(cc.length) };
    }
  }
  return { countryCode: fallback.code, national: digits };
}

const ContactEditDialog = ({
  open,
  onClose,
  contact = null,
  onSave,
  availableTags = [],
}) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    tags: [],
  });
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState("");
  const [countryCode, setCountryCode] = useState(COUNTRY_CODES[1].code);
  const isEditMode = !!contact;

  useEffect(() => {
    if (contact) {
      const { countryCode: foundCode, national } = splitPhoneCountry(
        contact.getPhoneNumber() || ""
      );
      setCountryCode(foundCode);
      setFormData({
        name: contact.getName() || "",
        phone: national,
        email: contact.getEmail() || "",
        tags: (contact.getTags() || []).map((tag) => ({
          id: tag.getId?.() || tag.id,
          name: tag.getName?.() || tag.name,
        })),
      });
    } else {
      setCountryCode(COUNTRY_CODES[1].code);
      setFormData({
        name: "",
        phone: "",
        email: "",
        tags: [],
      });
    }
    setErrors({});
  }, [contact, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone number format";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      let phone = formData.phone.trim();
      if (phone.startsWith("+")) phone = phone.slice(1);
      let phoneWithCode = phone;
      if (!phone.startsWith(countryCode.replace("+", ""))) {
        phoneWithCode = countryCode + phone;
      } else {
        phoneWithCode = "+" + phone;
      }
      onSave({ ...formData, phone: phoneWithCode });
      onClose();
    }
  };

  const handleAddTag = () => {
    const matchedTag = availableTags.find(
      (tag) => tag.getName().toLowerCase() === newTag.trim().toLowerCase()
    );
  
    if (
      newTag.trim() &&
      matchedTag &&
      !formData.tags.find((t) => t.id === matchedTag.getId())
    ) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, { id: matchedTag.getId(), name: matchedTag.getName() }],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagIdToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag.id !== tagIdToRemove),
    }));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ bgcolor: "#1976d2", width: 32, height: 32 }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6">
              {isEditMode ? "Edit Contact" : "Add New Contact"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Name Field */}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          {/* Phone Field with Country Code as Adornment */}
          <TextField
            label="Phone Number"
            value={formData.phone}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, phone: e.target.value }))
            }
            error={!!errors.phone}
            helperText={errors.phone}
            fullWidth
            required
            placeholder="1234567890"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ p: 0 }}>
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    size="small"
                    variant="standard"
                    disableUnderline
                    sx={{ minWidth: 70, fontSize: 14, background: 'none', '.MuiSelect-select': { padding: '0 8px' } }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.code}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ),
            }}
          />

          {/* Email Field */}
          <TextField
            label="Email (Optional)"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            placeholder="john@example.com"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          {/* Tags Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
              Tags
            </Typography>

            {/* Existing Tags */}
            {formData.tags.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    onDelete={() => handleRemoveTag(tag.id)}
                    size="small"
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      "& .MuiChip-deleteIcon": {
                        color: "#1976d2",
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Add New Tag */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Autocomplete
                freeSolo
                options={availableTags.filter(
                  (tag) => !formData.tags.find((selected) => selected.id === tag.getId())
                ).map((tag) => tag.getName())}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                value={newTag}
                onChange={(_, value) => {
                  if (typeof value === "string") {
                    setNewTag(value);
                  } else if (value && value.name) {
                    setNewTag(value.name);
                  } else {
                    setNewTag("");
                  }
                }}
                onInputChange={(_, value) => setNewTag(value)}
                onKeyPress={handleKeyPress}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add a tag..."
                    size="small"
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
                sx={{ borderRadius: 2, minWidth: "auto", px: 2 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ borderRadius: 2 }}
        >
          {isEditMode ? "Update Contact" : "Add Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactEditDialog;
